import { enLocale } from '@/translations/en'
import { AiFeature, EbomPartSnoozeStatus, PermissionOrganization } from '@/generated/graphql'

export type AllLocales = {
  [index: string]: Locale
}
export type Locale = {
  button: {
    save: string
    ok: string
    cancel: string
    close: string
    add: string
    create: string
    edit: string
    delete: string
    view: string
    viewUsages: string
    copy: string
    next: string
    previous: string
    discard: string
    archive: string
    unarchive: string
  }
  language: {
    [key: string]: string
  }
  notification: {
    failed: string
    saved: string
    duplicated: string
    deleted: string
    deleteTitle: string
    deleteWarningTitle: string
    deleteHindranceText: string
  }
  aiFeature: {
    [key in AiFeature]: string
  }
  navigationDrawer: {
    platform: string
    organization: string
    currentUploads: string
  }
  section: {
    processEditor: string
    operations: string
    checks: string
    issues: string
  }
  view: {
    home: {
      welcome: string
      selectOrganization: string
      buttonGo: string
    }
    issueLabel: {
      singular: string
      plural: string
      deleteWarning: string
      field: {
        name: string
        color: string
      }
    }
    organization: {
      bops: {
        buttonCreateEmpty: string
        buttonDuplicate: string
        cannotCreateMessage: string
        releaseTooltip: string
      }
      library: {
        title: string
        libraryStep: string
        createStep: string
        lastModified: string
        usage: string
        emptySteps: string
        whereUsedTitle: string
        usedInBops: string
        notInUse: string
        versions: string
        releasedAt: string
        releasedReadOnly: string
        versionReleased: string
        onOlderVersions: string
        releaseTitle: string
        releaseMessage: string
      }
      bopProcess: {
        downloadMbomButton: string
        downloadWorkInstructionsButton: string
        menuView: string
        menuAddChild: string
        menuAddParent: string
        menuUpdateNode: string
        menuDeleteParent: string
        menuDeleteNode: string
        menuMoveAbove: string
      }
      home: {
        productDescription: string
        operatorTitle: string
        operatorDescription: string
        productionBatchDescription: string
        issueDescription: string
      }
      module: {
        downloadMbomButton: string
        downloadWorkInstructionsButton: string
      }
      operatorProduct: {
        title: string
        titleBatchesInProgress: string
        noData: string
      }
      settings: {
        title: string
        tabAi: string
        aiExplanation: string
      }
      operatorProductConfiguration: {
        noData: string
        bopSelectorHint: string
        buttonCreateProductionBatch: string
        buttonViewModule: string
      }
    }
  }
  component: {
    accountMenu: {
      switchOrganization: string
      logOut: string
    }
    agreeToTerms: {
      title: string
      text: string
      labelReadCheck: string
      buttonAgree: string
    }
    apiErrorSnackbar: {
      error: {
        unknown: {
          title: string
          message: string
        }
        apiUnreachable: {
          title: string
          message: string
        }
        privileges: {
          title: string
          message: string
        }
        validation: {
          title: string
          message: string
        }
      }
      actionMoreDetails: string
      actionDismiss: string
      detailsTitle: string
      detailsClose: string
      detailsCopy: string
    }
    bopModelViewer: {
      noModel: string
      converting: string
      convertingHint: string
      conversionFailed: string
      loadingModel: string
      downloadFailed: string
      modelFailed: string
      zoomToSelection: string
      hidePreviousSteps: string
      showSubmodule: string
      showEverything: string
      fullscreen: string
      exitFullscreen: string
      resetCamera: string
      saveViewport: string
      selectPrompt: string
      move: string
      rotate: string
      addToStep: string
      removeFromStep: string
      allocateAllInstances: string
      instanceOf: string
      selectInstances: string
      instanceSelectionPrompt: string
      pickInstances: string
      closeSelection: string
      explode: string
      autoExplode: string
      explodeOverrideTitle: string
      explodeOverrideMessage: string
      resetTransforms: string
      highlightMismatch: string
      allocated: string
      alreadyAllocated: string
      cannotAllocate: string
      showingEbom: string
      ebomTree: string
      showEbomTree: string
      hideEbomTree: string
      resizeEbomTree: string
      ebomTreeNotSelectable: string
      hideArticle: string
      showArticle: string
      hiddenWithAssembly: string
      showAllArticles: string
      controlsHelpTitle: string
      controlsHelpShow: string
      controlsHelpHide: string
      controlRotate: string
      controlRotateGesture: string
      controlPan: string
      controlPanGesture: string
      controlPanAlternative: string
      controlZoom: string
      controlZoomGesture: string
      controlRotateTouch: string
      controlPanTouch: string
      controlZoomTouch: string
      controlZoomToPart: string
      controlZoomToPartGesture: string
    }
    viewerModelPicker: {
      title: string
      menuTitle: string
      automatic: string
      none: string
      useAutomatic: string
      useAutomaticHint: string
    }
    bopNavigationDrawer: {
      headerTimePerModule: string
      headerAssociatedEboms: string
      headerAssociatedIssues: string
      headerAssociatedUploads: string
    }
    bopUploads: {
      addButton: string
      active: string
      queued: string
      converting: string
      conversionFailed: string
    }
    bopSelector: {
      buttonModules: string
      buttonBOPs: string
    }
    articleAllocationList: {
      source: string
      sourceManual: string
      sourceEbom: string
      inEbom: string
      allocatedToSteps: string
    }
    bopModulesTimeChart: {
      headerTotalTime: string
    }
    bopReleaseConfirmation: {
      title: string
      message: string
    }
    bulkAllocationSuggestionActions: {
      createTitle: string
      createButton: string
      createConfirmation: string
      acceptTitle: string
      acceptButton: string
      acceptConfirmation: string
      rejectTitle: string
      rejectButton: string
      rejectConfirmation: string
    }
    completeProductionBatch: {
      title: string
      text: string
      incompleteChecks: string
      buttonActivator: string
      buttonCancel: string
      buttonSave: string
    }
    stepChecks: {
      noProductionBatchHint: string
    }
    archive: {
      tabActive: string
      tabArchived: string
      noArchivedItems: string
      archiveProductMessage: string
      unarchiveProductMessage: string
      archiveConfigurationMessage: string
      unarchiveConfigurationMessage: string
      unarchiveConfigurationWithProductMessage: string
      productArchived: string
      unarchiveProduct: string
      configurationArchived: string
      openConfigurations: string
    }
    releaseConfirmationDialog: {
      confirmation: string
    }
    confirmDialog: {
      title: string
      ok: string
      cancel: string
    }
    createIssueDialog: {
      title: string
      linkToSerialNumbers: string
      labels: string
    }
    createStep: {
      title: string
    }
    createStepUploads: {
      title: string
    }
    csvEbomLoader: {
      headerEbom: string
      headerColumns: string
      instructionColumns: string
      ebomName: string
      encoding: string
      rootArticleNumber: string
      hierarchyType: string
      hierarchyTypePositionBased: string
      hierarchyTypeParentBased: string
      warningParentBased: string
      columnArticleNumber: string
      columnArticleName: string
      columnArticleRevision: string
      columnUnit: string
      columnQuantity: string
      columnPosition: string
      columnParentArticleNumber: string
      buttonProcess: string
    }
    dataTable: {
      loading: string
      noData: string
      page: string
      itemsPerPage: string
      moreData: string
    }
    deleteDialog: {
      title: string
      warningTitle: string
      hindranceText: string
      deleteText: string
      // Old
      areYouSure: string
      delete: string
      cancel: string
    }
    downloadMbomDialog: {
      title: string
      closeButton: string
      downloadBopMbom: string
    }
    downloadModuleMbomButton: {
      labelLong: string
      labelShort: string
    }
    downloadWorkInstructionsDialog: {
      title: string
      closeButton: string
    }
    downloadModuleWorkInstructionsButton: {
      labelLong: string
      labelShort: string
    }
    duplicateModule: {
      title: string
    }
    duplicateStep: {
      title: string
    }
    ebomBulkAllocateDialog: {
      title: string
      text: string
    }
    cross3dModelWarning: {
      title: string
      body: string
      useCurrent: string
      useNew: string
    }
    ebomDiffDialog: {
      title: string
      selectLabel: string
      diffButton: string
      closeButton: string
    }
    ebomDiffSameParts: {
      title: string
      swapExplanation: string
      swapButton: string
      notificationSwapped: string
    }
    ebomDiffTree: {
      noDifferences: string
    }
    ebomLoader: {
      titlePickEbom: string
      titleLoadEbom: string
      titlePromoteEbomParts: string
      titleEbomPartAllocationSuggestions: string
      titleDone: string
      loadEbomFromCsv: string
      loadEbomFromStepFile: string
      doneHeader: string
      doneMessage: string
      doneButton: string
    }
    stepEbomLoader: {
      ebomName: string
      buttonProcess: string
    }
    ebomPartAllocationSuggestionsLoader: {
      header: string
      message: string
      skipButton: string
      createSuggestionsButton: string
      successMessage: string
    }
    ebomPartDetails: {
      buttonPromoteEbomPart: string
      buttonSnoozeEbomPart: string
      buttonUnSnoozeEbomPart: string
      quantityAllocatedToSteps: string
      headerStepAllocations: string
      tableHeaderModule: string
      tableHeaderStep: string
      tableHeaderQuantity: string
      promoteDialogTitle: string
      promoteDialogContent: string
      labelSuggestion: string
    }
    ebomPartSnoozeFilters: {
      explanation: string
    }
    ebomPartTree: {
      title: string
      filterOpenSuggestionsHint: string
      filterAllocationDisparityHint: string
      filterSnoozedHint: string
    }
    editDialog: {
      save: string
      cancel: string
      title: {
        create: string
        edit: string
        createWithEntity: string
        editWithEntity: string
      }
    }
    helpDialog: {
      title: string
      text: string
    }
    iconArchived: {
      archived: string
      notArchived: string
    }
    iconConsumable: {
      title: string
    }
    iconEbom: {
      title: string
    }
    iconEbomPartSnoozeStatus: {
      snoozed: string
      notSnoozed: string
      matchesArticleNumberAndPath: string
      matchesRevision: string
      notMatchesRevision: string
      matchesQuantity: string
      notMatchesQuantity: string
    }
    iconIssue: {
      title: string
    }
    iconManual: {
      title: string
    }
    iconPart: {
      title: string
    }
    iconProductionBatch: {
      title: string
    }
    issueCommentList: {
      buttonUnresolve: string
      buttonResolve: string
    }
    mainAppBar: {
      buttonOrgHome: string
      buttonOperator: string
    }
    operatorCurrentProductionBatch: {
      title: string
      deselectButton: string
    }
    operatorModuleStepper: {
      finalizeTitle: string
      doneTitle: string
      previousButton: string
      nextButton: string
      issueButton: string
      viewChanges: string
      diffDialogDismiss: string
    }
    stepChangeBanner: {
      title: string
      previousVersion: string
      currentVersion: string
      noPreviousVersion: string
      newStep: string
      removedStep: string
    }
    operatorFontSizeSelector: {
      label: string
      options: {
        small: string
        medium: string
        large: string
        extraLarge: string
      }
    }
    operatorProductionBatchDrawer: {
      titleBatchesInProgress: string
    }
    operatorStepTree: {
      title: string
      changedDescription: string
      addedDescription: string
      removedDescription: string
      movedDescription: string
      movedFromDescription: string
    }
    organizationPermissionsPicker: {
      inheritanceExplanation: string
    }
    pageHeader: {
      createButton: string
    }
    promoteEbomParts: {
      title: string
      instructions: string
      buttonSelectNonAssemblies: string
      buttonPromote: string
      buttonDone: string
      successMessage: string
    }
    siteButtonPicker: {
      noData: string
    }
    stepPickerDialog: {
      title: string
      select: string
    }
    stepProposalsConverter: {
      titleNoSteps: string
      textNoSteps: string
      titleSteps: string
      textSteps: string
    }
    stepProposalsFromFileDialog: {
      titlePickFile: string
      titleLoad: string
      titleConvertToSteps: string
      explanationTitle: string
      explanationText: string
      explanationMedia: string
      explanationPdf: string
      explanationDocx: string
      disclaimerTitle: string
      disclaimerText: string
      inputUpload: string
      buttonCreateProposals: string
      errorTitle: string
      errorText: string
    }
    stepProposalsProgress: {
      optimizing: {
        title: string
        message: string
      }
      extracting: {
        title: string
        message: string
      }
      clustering: {
        title: string
        message: string
      }
      done: {
        title: string
        message: string
      }
    }
    stepProposalsFromMediaLoader: {
      inputLanguage: string
      outputLanguage: string
      outputLanguageSameAsInput: string
      averageStepDuration: string
      averageStepDurationHint: string
      additionalPrompt: string
      additionalPromptHint: string
    }
    stepProposalsFromPdfLoader: {
      inputFirstPage: string
      inputLastPage: string
      inputLanguage: string
      additionalPrompt: string
      additionalPromptHint: string
    }
    stepProposalsFromDocxLoader: {
      inputLanguage: string
      additionalPrompt: string
      additionalPromptHint: string
    }
    stepTree: {
      title: string
      buttonProposeFromFile: string
      buttonPreview: string
      buttonAdd: string
      buttonAddEmpty: string
      buttonCreate: string
      buttonCopy: string
      buttonAddFromLibrary: string
      buttonAddInSection: string
      buttonAddSection: string
      buttonGroupIntoSection: string
      libraryStep: string
      emptySectionHint: string
    }
    createSectionDialog: {
      hint: string
    }
    updateStep: {
      sectionHint: string
    }
    stepPreviewDialog: {
      button: string
      title: string
      hint: string
    }
    stepOutputsSection: {
      buttonAssign: string
      buttonSaveAndAssign: string
      assignHint: string
      assignSameStep: string
    }
    stepDropDecisionDialog: {
      title: string
      message: string
      optionBundle: string
      optionMove: string
      optionMoveUnavailable: string
      optionMoveHint: string
      ioWarning: string
      summaryAllocations: string
      summaryInputs: string
      summaryOutputs: string
      summaryTools: string
      summaryInstructions: string
      summaryWarning: string
      summaryIcons: string
      summaryDuration: string
    }
    addFromLibrary: {
      title: string
      preview: string
      empty: string
    }
    acceptLibraryStepUpdate: {
      buttonActivator: string
      title: string
      updateInDraft: string
      updateInNewDraft: string
      updateAffectsAllUses: string
      usedIn: string
      buttonAccept: string
      updateApplied: string
      draftCreated: string
    }
    libraryStepStatus: {
      updateAvailable: string
      updateAvailableMessage: string
      versions: string
      detached: string
      detachedMessage: string
    }
    libraryStepVersionChange: {
      currentVersion: string
      newVersion: string
    }
    libraryStepReleaseConfirmation: {
      title: string
      message: string
      affectedBops: string
      bopStatus: string
      usedVersion: string
      searchPlaceholder: string
      released: string
    }
    libraryStepLock: {
      title: string
      message: string
      editInLibrary: string
      unlink: string
      unlinkTitle: string
      unlinkConfirm: string
    }
    stepToolbar: {
      buttonDelete: string
    }
    promoteStepToModule: {
      button: string
      title: string
      hintStep: string
      hintSubsteps: string
    }
    textAreaField: {
      markdownDetails: string
    }
    richTextField: {
      linkUrl: string
      apply: string
      placeholder: string
    }
    uploadButton: {
      instructionsEnabled: string
      instructionsDisabled: string
    }
    uploadCard: {
      disableDeleteBecauseUsed: string
    }
    uploadPicker: {
      title: string
      search: string
      maybeMoreItems: string
      selectButton: string
      closeButton: string
      newUploadButton: string
      tableItemsPerPage: string
      tableNoData: string
    }
    upsertDialog: {
      title: {
        create: string
        update: string
        createWithEntity: string
        updateWithEntity: string
      }
    }
    userPermissions: {
      title: string
      organizationPermissions: string
      addOrganization: string
      field: {
        userPermissions: string
      }
    }
    userProductionBatches: {
      buttonOtherUsers: string
      buttonTransferBatch: string
      confirmTransferText: string
    }
    versionStatus: {
      status: {
        RELEASED: string
        DRAFT: string
      }
    }
  }
  validation: {
    abbreviation: {
      regex: string
      unique: string
    }
    filename: {
      regex: string
    }
    isChecked: {
      notChecked: string
    }
    maxDifference: {
      message: string
    }
    milliQuantity: {
      min: string
      max: string
    }
    normalName: {
      regex: string
    }
    normalDescription: {
      regex: string
    }
    orgSlug: {
      pattern: string
      taken: string
    }
    unique: {
      unique: string
    }
    userEmail: {
      taken: string
    }
  }
  permission: {
    [k in PermissionOrganization]: TranslatedPermission
  }
  entity: {
    issueLabel: TranslatedEntity & {
      field: {
        name: string
        color: string
      }
    }
    article: TranslatedEntity & {
      field: {
        articleNumber: string
        revision: string
        name: string
        unit: string
        quantity: string
        type: string
        source: string
      }
      type: {
        PART: string
        CONSUMABLE: string
      }
      unit: {
        full: {
          GRAM: string
          KILOGRAM: string
          LITER: string
          MILLILITER: string
          METER: string
          MILLIMETER: string
          PIECES: string
        }
        abbreviated: {
          GRAM: string
          KILOGRAM: string
          LITER: string
          MILLILITER: string
          METER: string
          MILLIMETER: string
          PIECES: string
        }
      }
      validation: {
        articleAndRevisionTaken: string
      }
      disableEditBecauseReleased: string
      disableDeleteBecauseReleased: string
    }
    bop: TranslatedEntity & {
      field: {
        version: string
        versionStatus: string
      }
    }
    check: TranslatedEntity & {
      uploadButton: string
      uploadUsage: string
      field: {
        poNumber: string
        serialNumber: string
        reportingUser: string
        released: string
        value: string
        confirmed: string
        milliQuantity: string
        text: string
        upload: string
        updatedAt: string
        createdAt: string
      }
    }
    checkConfiguration: TranslatedEntity & {
      suggestions: string
      field: {
        type: string
        description: string
        upperLimit: string
        lowerLimit: string
        unit: string
        isRequired: string
        allowedMimeTypes: string
      }
      type: {
        CONFIRMATION: string
        NUMERIC: string
        STRING: string
        FILE_UPLOAD: string
      }
      fileTypeOption: {
        any: string
        imageFiles: string
        pdf: string
      }
    }
    ebom: TranslatedEntity & {
      field: {
        name: string
      }
    }
    ebomPart: TranslatedEntity & {
      isPromoted: string
      field: {
        articleNumber: string
        revision: string
        name: string
        quantity: string
        articlePath: string
        position: string
        snoozeStatus: string
      }
    }
    ebomPartAllocation: TranslatedEntity
    ebomPartSnooze: {
      status: {
        [k in EbomPartSnoozeStatus]: string
      }
    }
    graphNode: TranslatedEntity & {
      field: {
        milliQuantity: string
      }
    }
    issue: TranslatedEntity & {
      field: {
        name: string
        description: string
        assignedToUsers: string
        uploads: string
        createdAt: string
        createdByUser: string
        updatedAt: string
        links: string
        resolvedAt: string
        resolved: string
      }
      filter: {
        assignedToUser: string
        isResolved: string
        poNumbers: string
        serialNumbers: string
        bopId: string
      }
    }
    issueComment: TranslatedEntity & {
      field: {
        description: string
        uploads: string
        uploadIds: string
        createdAt: string
        createdByUser: string
        resolvedAt: string
        resolved: string
        updatedAt: string
      }
      uploadUsage: string
    }
    issueLink: TranslatedEntity & {
      field: {
        bopId: string
        moduleId: string
        serialNumber: string
        serialNumbers: string
        poNumber: string
        articleId: string
      }
      type: {
        bop: string
        module: string
        step: string
        serialNumber: string
        article: string
      }
    }
    manualPartAllocation: TranslatedEntity
    module: TranslatedEntity & {
      field: {
        name: string
        abbreviation: string
        description: string
      }
    }
    organization: TranslatedEntity & {
      field: {
        name: string
        slug: string
      }
    }
    product: TranslatedEntity & {
      field: {
        name: string
        abbreviation: string
        description: string
        image: string
      }
    }
    productConfiguration: TranslatedEntity & {
      field: {
        name: string
        abbreviation: string
        description: string
        image: string
      }
    }
    productionBatch: TranslatedEntity & {
      exportExcel: string
      exportFailed: string
      field: {
        poNumber: string
        serialNumbers: string
        trackSerialNumbers: string
        completed: string
        assignedUser: string
        duration: string
      }
      filter: {
        poNumber: string
        serialNumbers: string
        completed: string
      }
      label: {
        noSerialNumbers: string
      }
    }
    site: TranslatedEntity & {
      field: {
        name: string
        abbreviation: string
        archived: string
      }
    }
    icon: TranslatedEntity & {
      deleteWarning: string
      missing: string
      uploadUsage: string
      field: {
        name: string
        image: string
      }
    }
    stepType: TranslatedEntity & {
      field: {
        name: string
        archived: string
        released: string
      }
    }
    step: TranslatedEntity & {
      field: {
        name: string
        stepType: string
        durationInSeconds: string
        instructionText: string
        images: string
        tools: string
        warning: string
        warningText: string
        icons: string
      }
    }
    stepSection: TranslatedEntity
    stepInput: TranslatedEntity & {
      field: {
        milliQuantity: string
      }
    }
    stepOutput: TranslatedEntity & {
      field: {
        milliQuantity: string
      }
    }
    stepUpload: TranslatedEntity & {
      field: {
        caption: string
        fragmentStart: string
        fragmentEnd: string
        fragmentLoop: string
      }
      snapshot: {
        dialogTitle: string
        caption: string
        noPermission: string
        created: string
      }
      imageEditor: {
        title: string
        mode: {
          annotate: string
          crop: string
        }
        tool: {
          select: string
          text: string
          rectangle: string
          ellipse: string
          line: string
          arrow: string
        }
        button: {
          color: string
          thickness: string
          undo: string
          redo: string
          delete: string
          resetCrop: string
          removeEdits: string
          maximize: string
          minimize: string
        }
        hint: {
          annotate: string
          crop: string
        }
        discardConfirmText: string
        imageLoadFailed: string
      }
      uploadUsage: string
    }
    bopUpload: {
      uploadUsage: string
    }
    tool: TranslatedEntity & {
      field: {
        name: string
        description: string
        site: string
        images: string
        archived: string
        released: string
      }
    }
    upload: TranslatedEntity & {
      field: {
        name: string
        contentType: string
        createdAt: string
        usageLocations: string
      }
      usageLocations: {
        none: string
      }
    }
    toolUpload: TranslatedEntity
    user: TranslatedEntity & {
      field: {
        name: string
        firstname: string
        lastname: string
        email: string
        needsPassword: string
      }
    }
  }
}
export type TranslatedEntity = {
  singular: string
  plural: string
  deleteWarning?: string
  disableDeleteBecause?: string
  editWarning?: string
  disableEditBecause?: string
  conceptExplanation?: string
}
export type TranslatedPermission = {
  name: string
  description: string
}

export const locales: AllLocales = {
  en: enLocale,
}
